import { useRef } from "react";
import { Carousel } from "@mantine/carousel";
import Autoplay from "embla-carousel-autoplay";
import { Flex, Text, rem } from "@mantine/core";

/** ISO 3166-1 alpha-2 → regional indicator flag emoji (e.g. IN → India flag). */
function regionalIndicatorFlagEmoji(alpha2: string): string | null {
  const c = alpha2.toUpperCase();
  if (c.length !== 2 || !/^[A-Z]{2}$/.test(c)) return null;
  const base = 0x1f1e6;
  const letterA = 0x41;
  return String.fromCodePoint(
    base + (c.charCodeAt(0) - letterA),
    base + (c.charCodeAt(1) - letterA),
  );
}

export type DiscoverPromoSlide = {
  id: string;
  /** Optional country codes (e.g. IN, CN) — flags row is omitted when empty */
  flags?: string[];
  headline: string;
  description: string;
  /** Mantine color token for the inner slide panel; default matches page body (white). Outer frame uses `primary.0` on the wrapping `Paper`. */
  surfaceBg?: string;
  /** Mantine color token for headline; default `primary.7` */
  headlineColor?: string;
  /** Mantine color token for body; default `slate.6` */
  descriptionColor?: string;
};

export type DiscoverPromoCarouselProps = {
  slides: DiscoverPromoSlide[];
  height?: number;
  autoplayDelayMs?: number;
};

/** Horizontal padding inside slides when prev/next controls are shown (control + offset + buffer). */
const SLIDE_INSET_WITH_CONTROLS = rem(50);

function DiscoverPromoSlideCard({
  slide,
  multi,
}: {
  slide: DiscoverPromoSlide;
  multi: boolean;
}) {
  const codes = slide.flags?.map((c) => c.toUpperCase()).filter(Boolean) ?? [];

  return (
    <Flex
      w="100%"
      maw="100%"
      mih={0}
      h="100%"
      direction="column"
      justify="center"
      align="center"
      gap="sm"
      px={multi ? SLIDE_INSET_WITH_CONTROLS : rem(12)}
      py={rem(10)}
      bg={slide.surfaceBg ?? "var(--mantine-color-body)"}
      style={{
        borderRadius: "var(--mantine-radius-md)",
        boxSizing: "border-box",
      }}
    >
      <Flex w="100%" maw="100%" miw={0} direction="column" align="center" gap="xs">
        {codes.length > 0 ? (
          <Flex gap={10} justify="center" align="center" wrap="nowrap">
            {codes.map((code) => {
              const flag = regionalIndicatorFlagEmoji(code);
              if (!flag) return null;
              return (
                <Text key={code} span fz={24} lh={1} role="img" aria-label={`${code} flag`}>
                  {flag}
                </Text>
              );
            })}
          </Flex>
        ) : null}
        <Text
          fw={700}
          size="sm"
          c={slide.headlineColor ?? "primary.7"}
          ta="center"
          maw="100%"
          lineClamp={3}
          style={{ wordBreak: "break-word" }}
        >
          {slide.headline}
        </Text>
        <Text
          size="xs"
          c={slide.descriptionColor ?? "slate.6"}
          lh={1.55}
          ta="center"
          maw="100%"
          style={{ wordBreak: "break-word" }}
        >
          {slide.description}
        </Text>
      </Flex>
    </Flex>
  );
}

export default function DiscoverPromoCarousel({
  slides,
  height = 200,
  autoplayDelayMs = 4500,
}: DiscoverPromoCarouselProps) {
  const autoplay = useRef(
    Autoplay({
      delay: autoplayDelayMs,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      stopOnFocusIn: false,
    }),
  );

  if (slides.length === 0) {
    return null;
  }

  const multi = slides.length > 1;

  return (
    <Carousel
      height={height}
      w="100%"
      maw="100%"
      withControls={multi}
      withIndicators={multi}
      loop={multi}
      plugins={multi ? [autoplay.current] : []}
      onMouseEnter={multi ? autoplay.current.stop : undefined}
      onMouseLeave={multi ? autoplay.current.reset : undefined}
      controlSize={22}
      controlsOffset={4}
      styles={{
        root: {
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
        },
        viewport: { maxWidth: "100%", boxSizing: "border-box" },
        container: { maxWidth: "100%" },
        controls: {
          paddingInline: rem(2),
        },
        control: {
          opacity: 0.92,
        },
        indicators: { bottom: rem(8) },
        indicator: { width: 6, height: 6 },
      }}
    >
      {slides.map((slide) => (
        <Carousel.Slide key={slide.id}>
          <DiscoverPromoSlideCard slide={slide} multi={multi} />
        </Carousel.Slide>
      ))}
    </Carousel>
  );
}
